import { MarketOutcome, PeriodType } from '../../enums';
import { MarketConfig } from '../../interfaces';
import {
	buildAsianHandicapQuestion,
	buildBothTeamsToScoreQuestion,
	buildCorrectScoreQuestion,
	buildDoubleChanceQuestion,
	buildHalftimeFulltimeQuestion,
	buildMoneylineQuestion,
	buildTeamTotalQuestion,
	buildTeamTotalStatQuestion,
	buildTotalGoalsQuestion,
	buildTotalStatQuestion,
	getPeriodScores,
	resolveAsianHandicap,
	resolveBothTeamsToScore,
	resolveCorrectScore,
	resolveDoubleChance,
	resolveHalftimeFulltime,
	resolveTeamTotal,
	resolveTeamTotalStat,
	resolveTotalStat,
} from '../../utils/market/soccer.utils';
import {
	extractNumber,
	extractSelection,
	getPlayerResult,
	getPlayerStats,
	resolveCondition,
	resolveFirstTeamToScore,
	resolveGamePlayerProp,
	resolveMoneyline,
	resolveTotalPoints,
	resolveTotalPointsOddEven,
} from '../../utils';

export const soccerConfig: Record<string, MarketConfig> = {
	// Main Lines
	Moneyline: {
		buildQuestion: (m) => buildMoneylineQuestion(m.selection, PeriodType.Game),
		isFinalRequired: true,
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.Game),
	},
	'1st Half Moneyline': {
		buildQuestion: (m) => buildMoneylineQuestion(m.selection, PeriodType.Period1),
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.Period1),
	},
	'2nd Half Moneyline': {
		buildQuestion: (m) => buildMoneylineQuestion(m.selection, PeriodType.Period2),
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.Period2),
	},
	'Double Chance': {
		buildQuestion: (m) => buildDoubleChanceQuestion(m.name, PeriodType.Game),
		isFinalRequired: true,
		resolve: (q, gc) => resolveDoubleChance(q, gc, PeriodType.Game),
	},
	'1st Half Double Chance': {
		buildQuestion: (m) => buildDoubleChanceQuestion(m.name, PeriodType.Period1),
		resolve: (q, gc) => resolveDoubleChance(q, gc, PeriodType.Period1),
	},
	'2nd Half Double Chance': {
		buildQuestion: (m) => buildDoubleChanceQuestion(m.name, PeriodType.Period2),
		resolve: (q, gc) => resolveDoubleChance(q, gc, PeriodType.Period2),
	},
	'Total Goals': {
		buildQuestion: (m) => buildTotalGoalsQuestion(m.points, PeriodType.Game),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Game),
	},
	'1st Half Total Goals': {
		buildQuestion: (m) => buildTotalGoalsQuestion(m.points, PeriodType.Period1),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period1),
	},
	'2nd Half Total Goals': {
		buildQuestion: (m) => buildTotalGoalsQuestion(m.points, PeriodType.Period2),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period2),
	},
	'Total Goals Odd/Even': {
		buildQuestion: () => 'Will the total goals be even?',
		isFinalRequired: true,
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Game),
	},

	// Handicap
	'Asian Handicap': {
		buildQuestion: (m) => buildAsianHandicapQuestion(m.selection, m.points, PeriodType.Game),
		isFinalRequired: true,
		resolve: (q, gc) => resolveAsianHandicap(q, gc, PeriodType.Game),
	},
	'1st Half Asian Handicap': {
		buildQuestion: (m) => buildAsianHandicapQuestion(m.selection, m.points, PeriodType.Period1),
		resolve: (q, gc) => resolveAsianHandicap(q, gc, PeriodType.Period1),
	},
	'2nd Half Asian Handicap': {
		buildQuestion: (m) => buildAsianHandicapQuestion(m.selection, m.points, PeriodType.Period2),
		resolve: (q, gc) => resolveAsianHandicap(q, gc, PeriodType.Period2),
	},

	// Team Props
	'Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Game),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(q, gc, PeriodType.Game),
	},
	'1st Half Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Period1),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(q, gc, PeriodType.Period1),
	},
	'2nd Half Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Period2),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(q, gc, PeriodType.Period2),
	},
	'Both Teams To Score': {
		buildQuestion: () => buildBothTeamsToScoreQuestion(PeriodType.Game),
		resolve: (q, gc) => resolveBothTeamsToScore(q, gc, PeriodType.Game),
	},
	'1st Half Both Teams To Score': {
		buildQuestion: () => buildBothTeamsToScoreQuestion(PeriodType.Period1),
		resolve: (q, gc) => resolveBothTeamsToScore(q, gc, PeriodType.Period1),
	},
	'2nd Half Both Teams To Score': {
		buildQuestion: () => buildBothTeamsToScoreQuestion(PeriodType.Period2),
		resolve: (q, gc) => resolveBothTeamsToScore(q, gc, PeriodType.Period2),
	},
	'First Team To Score': {
		buildQuestion: (m) => `Will ${m.selection} score first?`,
		resolve: (q, gc) => resolveFirstTeamToScore(getPeriodScores, q, gc, PeriodType.Game),
	},

	// Correct Score
	'Correct Score': {
		buildQuestion: (m) => buildCorrectScoreQuestion(m.selection_line, PeriodType.Game),
		resolve: (q, gc) => resolveCorrectScore(q, gc, PeriodType.Game),
	},
	'1st Half Correct Score': {
		buildQuestion: (m) => buildCorrectScoreQuestion(m.selection_line, PeriodType.Period1),
		resolve: (q, gc) => resolveCorrectScore(q, gc, PeriodType.Period1),
	},
	'2nd Half Correct Score': {
		buildQuestion: (m) => buildCorrectScoreQuestion(m.selection_line, PeriodType.Period2),
		resolve: (q, gc) => resolveCorrectScore(q, gc, PeriodType.Period2),
	},
	'Halftime / Fulltime': {
		buildQuestion: (m) => buildHalftimeFulltimeQuestion(m.selection),
		resolve: (q, gc) => resolveHalftimeFulltime(q, gc),
	},

	// Corners
	'Total Corners': {
		buildQuestion: (m) => buildTotalStatQuestion(m.points, PeriodType.Game, 'corners'),
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveTotalStat(q, gc, r, 'corners', PeriodType.Game),
	},
	'1st Half Total Corners': {
		buildQuestion: (m) => buildTotalStatQuestion(m.points, PeriodType.Period1, 'corners'),
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveTotalStat(q, gc, r, 'corners', PeriodType.Period1),
	},
	'2nd Half Total Corners': {
		buildQuestion: (m) => buildTotalStatQuestion(m.points, PeriodType.Period2, 'corners'),
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveTotalStat(q, gc, r, 'corners', PeriodType.Period2),
	},
	'Team Total Corners': {
		buildQuestion: (m) =>
			buildTeamTotalStatQuestion(m.selection, m.points, PeriodType.Game, 'corners'),
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveTeamTotalStat(q, gc, r, 'corners', PeriodType.Game),
	},
	'1st Half Team Total Corners': {
		buildQuestion: (m) =>
			buildTeamTotalStatQuestion(m.selection, m.points, PeriodType.Period1, 'corners'),
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveTeamTotalStat(q, gc, r, 'corners', PeriodType.Period1),
	},
	// Cards & Shots
	'Total Cards': {
		buildQuestion: (m) => buildTotalStatQuestion(m.points, PeriodType.Game, 'cards'),
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveTotalStat(q, gc, r, 'cards', PeriodType.Game),
	},
	'1st Half Total Cards': {
		buildQuestion: (m) => buildTotalStatQuestion(m.points, PeriodType.Period1, 'cards'),
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveTotalStat(q, gc, r, 'cards', PeriodType.Period1),
	},
	'Team Total Cards': {
		buildQuestion: (m) =>
			buildTeamTotalStatQuestion(m.selection, m.points, PeriodType.Game, 'cards'),
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveTeamTotalStat(q, gc, r, 'cards', PeriodType.Game),
	},
	'1st Half Team Total Cards': {
		buildQuestion: (m) =>
			buildTeamTotalStatQuestion(m.selection, m.points, PeriodType.Period1, 'cards'),
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveTeamTotalStat(q, gc, r, 'cards', PeriodType.Period1),
	},
	'Total Shots': {
		buildQuestion: (m) => buildTotalStatQuestion(m.points, PeriodType.Game, 'shots'),
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveTotalStat(q, gc, r, 'shots', PeriodType.Game),
	},
	'Team Total Shots': {
		buildQuestion: (m) =>
			buildTeamTotalStatQuestion(m.selection, m.points, PeriodType.Game, 'shots'),
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveTeamTotalStat(q, gc, r, 'shots', PeriodType.Game),
	},
	'Total Shots On Target': {
		buildQuestion: (m) => buildTotalStatQuestion(m.points, PeriodType.Game, 'shots on target'),
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveTotalStat(q, gc, r, 'shots_on_target', PeriodType.Game),
	},
	'Team Total Shots On Target': {
		buildQuestion: (m) =>
			buildTeamTotalStatQuestion(m.selection, m.points, PeriodType.Game, 'shots on target'),
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveTeamTotalStat(q, gc, r, 'shots_on_target', PeriodType.Game),
	},

	// Goal Scorers
	'Player Goals': {
		buildQuestion: (m) => `Will ${m.selection} score at least ${m.points} goals?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'player_goals', ['score at least']),
	},
	'Anytime Goal Scorer': {
		buildQuestion: (m) => `Will ${m.selection} score a goal?`,
		resolve: (q, gc, r) => {
			const playerName = extractSelection(q, 'Will ', ['score a goal']);
			const playerResult = getPlayerResult(playerName, r);
			const goals = playerResult?.player_goals ?? 0;

			return [resolveCondition(goals >= 1, gc.isCompleted), goals];
		},
	},
	'First Goal Scorer': {
		buildQuestion: (m) => `Will ${m.selection} score the first goal?`,
		resolve: (q, gc, r) => {
			const total = gc.homeScore + gc.awayScore;

			if (total === 0) {
				return [gc.isCompleted ? MarketOutcome.Lost : MarketOutcome.Pending];
			}

			const playerName = extractSelection(q, 'Will ', ['score the first goal']);
			const playerStats = getPlayerStats(playerName, r);

			return [playerStats?.first_goal_scorer === 1 ? MarketOutcome.Won : MarketOutcome.Lost];
		},
	},
	'Player Goals + Assists': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} goals + assists?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_goals_+_assists', ['record at least']),
	},
	'Player To Score Or Assist': {
		buildQuestion: (m) => `Will ${m.selection} score a goal or record an assist?`,
		resolve: (q, gc, r) => {
			const playerName = extractSelection(q, 'Will ', ['score a goal']);
			const stats = getPlayerResult(playerName, r);
			const total = (stats?.player_goals ?? 0) + (stats?.player_assists ?? 0);

			return [resolveCondition(total >= 1, gc.isCompleted), total];
		},
	},
	'Player To Score In Both Halves': {
		buildQuestion: (m) => `Will ${m.selection} score in both halves?`,
		resolve: (q, gc, r) => {
			const playerName = extractSelection(q, 'Will ', ['score in both halves']);
			const stats = r?.player_results?.find((p: any) => p.player?.name === playerName)?.stats ?? [];

			const goals1 = stats.find((s: any) => s.period === 'period_1')?.stats?.goals ?? 0;
			const goals2 = stats.find((s: any) => s.period === 'period_2')?.stats?.goals ?? 0;

			if (!gc.isScopeCompleted(PeriodType.Period1)) return [MarketOutcome.Pending];

			if (goals1 < 1) return [MarketOutcome.Lost];

			return [resolveCondition(goals2 >= 1, gc.isCompleted)];
		},
	},

	// Player Props
	'Player Assists': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} assists?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'player_assists', ['record at least']),
	},
	'Player Shots': {
		buildQuestion: (m) => `Will ${m.selection} have at least ${m.points} shots?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'player_shots', ['have at least']),
	},
	'1st Half Player Shots': {
		buildQuestion: (m) => `Will ${m.selection} 1st half shots be over ${m.points}?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const playerName = extractSelection(q, 'Will ', ['1st half shots be over']);
			const playerResult = getPlayerResult(playerName, r);
			const value = playerResult?.['player_shots'] ?? 0;

			return [resolveCondition(value >= threshold, gc.isScopeCompleted(PeriodType.Period1)), value];
		},
	},
	'Player Shots On Target': {
		buildQuestion: (m) => `Will ${m.selection} have at least ${m.points} shots on target?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_shots_on_target', ['have at least']),
	},
	'1st Half Player Shots On Target': {
		buildQuestion: (m) => `Will ${m.selection} 1st half shots on target be over ${m.points}?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, '1st_half_player_shots_on_target', [
				'1st half shots on target be over',
			]),
	},
	'Player Cards': {
		buildQuestion: (m) => `Will ${m.selection} receive at least ${m.points} cards?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'player_cards', ['receive at least']),
	},
	'Player Fouls': {
		buildQuestion: (m) => `Will ${m.selection} commit at least ${m.points} fouls?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'player_fouls', ['commit at least']),
	},
	'Player Fouls Drawn': {
		buildQuestion: (m) => `Will ${m.selection} draw at least ${m.points} fouls?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'player_fouls_drawn', ['draw at least']),
	},
	'Anytime Card Receiver': {
		buildQuestion: (m) => `Will ${m.selection} receive a card?`,
		resolve: (q, gc, r) => {
			const playerName = extractSelection(q, 'Will ', ['receive a card']);
			const playerResult = getPlayerResult(playerName, r);

			return [resolveCondition(playerResult?.player_cards ?? false, gc.isCompleted)];
		},
	},
};
